<script setup>
import { useCustomerStore } from "@/stores/CustomersStore"
import { useModalStore } from '@/stores/ModalStore'
import { useI18n } from "vue-i18n"
import { ref, onMounted, onUnmounted } from 'vue'
import { useDisplay } from "vuetify"
import CustomerData from "@/components/event/CustomerData.vue"
import { showErrorToast } from "@/utils/toast"

const { t } = useI18n()
const { mobile } = useDisplay()
const route = useRoute()

// Stores
const THIS_MODAL_NAME = 'addNewCustomer'
const modalStore = useModalStore()
const customerStore = useCustomerStore()

const customerDataComponent = ref(null) //reference to form with customer data (child component)

// Received customer data from the child component
const customerData = ref(null)

// const isCustomerInDatabase = ref(false) // Flag to indicate if the customer exists in the database
const foundCustomers = ref([]) // Array to store found customers

// 👉Add a new customer
// forceAdd": true/false -> add no matter if the customer exists or not
const addNewCustomer = async (forceAdd = false) => {
  try {
    const { valid } = await customerDataComponent.value.customerDataFormEl?.validate()
    if (valid) {
      // Trigger data submission and validation in the CustomerData component
      const customerFormData = await customerDataComponent.value.getCustomerFormData()

      if (!customerFormData) {
        console.error('No customer data received')

        return
      }
      customerData.value = customerFormData

      const newCustomer = {
        ...customerData.value,
        forceAdd: forceAdd,
      }

      const response = await customerStore.addCustomer(newCustomer)
      customerStore.clearCustomerStatistics() // Clear "Współczynnik nieodbytych wizyt ..."
      customerStore.clearSelectedCustomer()

      if (response.success) {

        await customerStore.setSelectedCustomer(response.data.salonerClient || newCustomer)
        // customerStore.customerVouchers = []
        // customerStore.customerTickets = []
        // customerStore.customerVouchersAndTickets = []
        // customerStore.incomingClientVisits = []
        // customerStore.clearCustomerStatistics()
        // customerStore.customerLoyaltyPoints = 0


        // Reload the customer table - IF ON CUSTOMER TAB
        if (route.path === '/clients') {
          customerStore.loadCustomers({
            page: customerStore.clientTabData.page,
            itemsPerPage: customerStore.clientTabData.itemsPerPage,
          }).then(() => {
            // Check if new client is visible in the datatable
            if(document.getElementsByClassName('salonerClientId-'+response.data.salonerClient.salonerClientId).length === 0) {
              showInfoToast(t('resetFiltersIfYouDontSeeNewClient'))
            }
          })
          showToast(t('addedCustomerSuccess'), 'success')
        } else {
          showToast(t('customerSelected'), 'success')
        }



        modalStore.closeModal(THIS_MODAL_NAME)
      } else {
        await customerStore.setSelectedCustomer(null)

      }
    } else {
      showErrorToast(t('form_has_errors'))
    }
  } catch (error) {
    console.error('Error adding new customer:', error)
  }
}

const clearFoundCustomers = () => {
  foundCustomers.value = []
  // isCustomerInDatabase.value = false
}

const isLoadingLocaly = ref(false)
// 👉 Check if the customer exists in the database by the phone, return found customers
const checkExistance = async (phone) => {
  // isCustomerInDatabase.value = false
  isLoadingLocaly.value = true
  try {
    const response = await customerStore.checkIfClientExists(phone)

    if (response.data?.clientExist) {
      // isCustomerInDatabase.value = true

      let numberFromResponse = response.data?.salonerClients?.[0]?.phone
      let numberFromInput = customerDataComponent.value.getPhoneValue()
      // console.log('numberFromResponse', numberFromResponse)
      // console.log('numberFromInput', numberFromInput)

      //Display a toast message if the phone number matches the one from the response
      if(numberFromInput.toString().includes(numberFromResponse.toString())) { //TODO: jak Magda poprawi BE to wrocic do normalnego sprawdzania
        foundCustomers.value = response.data?.salonerClients
        showToast(response.message, 'info')
      } else {
        // If the phone number does not match, clear the found customers
        clearFoundCustomers()
      }

    } else {
      clearFoundCustomers()
    }
  } catch (error) {
    console.error('Error checking customer existence:', error)
    clearFoundCustomers()
  } finally {
    isLoadingLocaly.value = false
  }

}

onUnmounted( () => {
  try {
    if(modalStore.focusAfterClose.length) {
      document.getElementById(modalStore.focusAfterClose).focus()
      modalStore.focusAfterClose = ""
      customerStore.searchedCustomer = ""
    }
  } catch (e) {
    console.error(e)
  }
  clearFoundCustomers()
  //reload
  customerStore.updateCustomersArray(customerData.value)

})
</script>

<template>
  <VDialog
    :model-value="modalStore.modals.addNewCustomer"
    width="720px"
    :persistent="customerStore.isLoading"
    :fullscreen="mobile"
    @update:model-value="(val) => modalStore.modals.addNewCustomer = val"
  >
    <VCard
      :loading="customerStore.isLoading"
      :disabled="customerStore.isLoading"
    >
      <template #title>
        <div class="d-flex justify-space-between justify-space-between  align-center">
          {{ $t('new_customer') }}
          <VIcon
            icon="ri-close-line"
            :disabled="customerStore.isLoading"
            @click="modalStore.closeModal(THIS_MODAL_NAME)"
          />
        </div>
      </template>
      <VCardText>
<!--          :show-alert-about-existing-client="isCustomerInDatabase"-->
        <CustomerData
          ref="customerDataComponent"
          :customer-data="customerStore.blankCustomer"
          :found-customers="foundCustomers"
          :is-loading-localy="isLoadingLocaly"
          @check-if-client-exist="phone => checkExistance(phone)"
          @clear-found-customers="foundCustomers = []"
        />

      </VCardText>
      <VCardActions>
        <div class="d-flex justify-space-between flex-fill px-2">
          <VBtn
            variant="outlined"
            color="secondary"
            :disabled="customerStore.isLoading"
            @click="modalStore.closeModal(THIS_MODAL_NAME)"
          >
            {{ $t('cancel') }}
          </VBtn>

          <VBtn
            color="green"
            variant="flat"
            :disabled="customerStore.isLoading"
            @click="addNewCustomer(true)"
          >

            {{ $t('add') }}
          </VBtn>
        </div>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style lang="scss">
@forward "../../../@core/scss/template/libs/vuetify/variables";

.highlight-box {
  background-color: rgba(224, 63, 4, 0.1) !important;
}
</style>

